import { useState } from 'react';
import { User } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { ROUTES } from '@/lib/routes';
import { InteractiveAvatar } from './InteractiveAvatar';

interface UserAvatarProps {
  userId?: string;
  userName?: string;
  avatarUrl?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  xs: 'h-4 w-4 text-3xs',
  sm: 'h-7 w-7 text-3xs',
  md: 'h-8 w-8 text-xs',
  lg: 'h-16 w-16 text-lg',
} as const;

const iconSizeClasses = {
  xs: 'h-2.5 w-2.5',
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-7 w-7',
} as const;

function InitialsFallback({ userName, size = 'sm' }: Pick<UserAvatarProps, 'userName' | 'size'>) {
  if (userName) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-full bg-muted font-medium text-muted-foreground shrink-0 ring-1 ring-border',
          sizeClasses[size],
        )}
      >
        {userName.charAt(0).toUpperCase()}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full bg-muted shrink-0 ring-1 ring-border',
        sizeClasses[size],
      )}
    >
      <User className={cn('text-muted-foreground', iconSizeClasses[size])} />
    </div>
  );
}

function AvatarContent({
  userName,
  avatarUrl,
  size = 'sm',
}: Pick<UserAvatarProps, 'userName' | 'avatarUrl' | 'size'>) {
  const [imgError, setImgError] = useState(false);

  if (avatarUrl && !imgError) {
    return (
      <img
        src={avatarUrl}
        alt={userName ?? ''}
        className={cn('rounded-full object-cover shrink-0 ring-1 ring-border', sizeClasses[size])}
        onError={() => setImgError(true)}
      />
    );
  }

  return <InitialsFallback userName={userName} size={size} />;
}

export function UserAvatar({
  userId,
  userName,
  avatarUrl,
  size = 'sm',
  className,
}: UserAvatarProps) {
  const content = <AvatarContent userName={userName} avatarUrl={avatarUrl} size={size} />;

  if (userId) {
    return (
      <InteractiveAvatar
        to={ROUTES.userProfile(userId)}
        className={className}
        title={userName ?? undefined}
        aria-label={userName ? `${userName}'s profile` : 'User profile'}
      >
        {content}
      </InteractiveAvatar>
    );
  }

  return (
    <span className={className} title={userName ?? undefined}>
      {content}
    </span>
  );
}
